import React, { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Switch,
  FormControlLabel, IconButton, CircularProgress,
  MenuItem, Chip, Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";

import { createPopup, updatePopup, previewCount } from "../../services/companyPopupService";
import usePlans from "../../hooks/usePlans";
import { getBackendUrl } from "../../config";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  imagePreview: {
    width: "100%",
    maxHeight: 160,
    objectFit: "cover",
    borderRadius: 8,
    marginTop: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  sectionLabel: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: 4,
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  quillWrapper: {
    "& .ql-editor": { minHeight: 100 },
    marginTop: 4,
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    borderRadius: 8,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    marginBottom: 6,
  },
  filterType: { flex: "0 0 auto", minWidth: 260 },
  filterValue: { flex: 1, minWidth: 100, maxWidth: 180 },
  addFilterBtn: { marginTop: 6 },
  operatorChip: {
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.75rem",
  },
  previewBox: {
    background: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: 8,
    padding: "8px 14px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
  },
  typeColors: {
    info: { background: "#e0f2fe", color: "#0369a1" },
    warning: { background: "#fef9c3", color: "#92400e" },
    success: { background: "#dcfce7", color: "#166534" },
    error: { background: "#fee2e2", color: "#991b1b" },
    announcement: { background: "#f3e8ff", color: "#7c3aed" },
  },
}));

const FILTER_TYPES = [
  { value: "all_companies", label: "Todas as empresas", hasValue: false },
  { value: "status_active", label: "Empresa ativa", hasValue: false },
  { value: "status_inactive", label: "Empresa inativa / bloqueada", hasValue: false },
  { value: "trial", label: "Em período de teste", hasValue: false },
  { value: "trial_days_min", label: "Em teste há mais de X dias", hasValue: "number", placeholder: "Ex: 7" },
  { value: "trial_days_max", label: "Em teste há menos de X dias", hasValue: "number", placeholder: "Ex: 3" },
  { value: "registered_days_max", label: "Cadastrada há menos de X dias (nova)", hasValue: "number", placeholder: "Ex: 7" },
  { value: "registered_days_min", label: "Cadastrada há mais de X dias", hasValue: "number", placeholder: "Ex: 30" },
  { value: "active_days_min", label: "Ativa há mais de X dias", hasValue: "number", placeholder: "Ex: 60" },
  { value: "overdue_invoice_days_min", label: "Fatura vencida há mais de X dias", hasValue: "number", placeholder: "Ex: 5" },
  { value: "due_date_expiring_days", label: "Vencimento nos próximos X dias", hasValue: "number", placeholder: "Ex: 5" },
  { value: "due_date_expired", label: "Vencimento já expirado", hasValue: false },
  { value: "no_login_days_min", label: "Sem login há mais de X dias", hasValue: "number", placeholder: "Ex: 15" },
  { value: "plan_id", label: "No plano específico", hasValue: "plan" },
  { value: "segment", label: "Por segmento", hasValue: "segment" },
  { value: "specific_companies", label: "Empresas específicas (IDs)", hasValue: "ids", placeholder: "Ex: 2,5,10" },
];

const SEGMENTS = ["tecnologia", "saúde", "educação", "varejo", "serviços", "indústria", "outros"];

const POPUP_TYPES = [
  { value: "info", label: "Informação (azul)" },
  { value: "warning", label: "Aviso (amarelo)" },
  { value: "success", label: "Sucesso (verde)" },
  { value: "error", label: "Alerta (vermelho)" },
  { value: "announcement", label: "Anúncio (roxo)" },
];

const emptyForm = {
  title: "",
  content: "",
  buttonText: "Entendi",
  buttonUrl: "",
  type: "info",
  active: true,
  dismissible: true,
  startDate: "",
  endDate: "",
};

const emptyFilters = { operator: "AND", conditions: [{ type: "all_companies" }] };

const CompanyPopupFormModal = ({ open, onClose, item, onSaved }) => {
  const classes = useStyles();
  const imageInputRef = useRef(null);

  const [form, setForm] = useState(emptyForm);
  const [filters, setFilters] = useState(emptyFilters);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isEdit = Boolean(item?.id);
  const { list: fetchPlans } = usePlans();

  useEffect(() => {
    fetchPlans().then((data) => setPlans(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (open) {
      if (item) {
        setForm({
          title: item.title || "",
          content: item.content || "",
          buttonText: item.buttonText || "Entendi",
          buttonUrl: item.buttonUrl || "",
          type: item.type || "info",
          active: item.active !== false,
          dismissible: item.dismissible !== false,
          startDate: item.startDate ? item.startDate.slice(0, 16) : "",
          endDate: item.endDate ? item.endDate.slice(0, 16) : "",
        });
        setFilters(item.filters || emptyFilters);
        setImagePreview(item.imageUrl ? `${backendUrl}/public/${item.imageUrl}` : null);
      } else {
        setForm(emptyForm);
        setFilters(emptyFilters);
        setImagePreview(null);
      }
      setImageFile(null);
      setPreviewResult(null);
    }
  }, [open, item]);

  const handleField = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggle = (field) => (e) => {
    const checked = e.target.checked;
    setForm((prev) => ({ ...prev, [field]: checked }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const toggleOperator = () => {
    setFilters((prev) => ({ ...prev, operator: prev.operator === "AND" ? "OR" : "AND" }));
  };

  const addCondition = () => {
    setFilters((prev) => ({
      ...prev,
      conditions: [...prev.conditions, { type: "all_companies" }],
    }));
  };

  const removeCondition = (idx) => {
    setFilters((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== idx),
    }));
  };

  const updateCondition = (idx, field, value) => {
    setFilters((prev) => {
      const conditions = [...prev.conditions];
      conditions[idx] = { ...conditions[idx], [field]: value };
      if (field === "type") delete conditions[idx].value;
      return { ...prev, conditions };
    });
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewResult(null);
    try {
      const { data } = await previewCount(filters);
      setPreviewResult(data);
    } catch {
      toast.error("Erro ao calcular prévia");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error("Título é obrigatório"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("content", form.content || "");
      fd.append("buttonText", form.buttonText || "Entendi");
      fd.append("buttonUrl", form.buttonUrl || "");
      fd.append("type", form.type || "info");
      fd.append("active", form.active ? "true" : "false");
      fd.append("dismissible", form.dismissible ? "true" : "false");
      fd.append("startDate", form.startDate || "");
      fd.append("endDate", form.endDate || "");
      fd.append("filters", JSON.stringify(filters));
      if (imageFile) fd.append("image", imageFile);

      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (isEdit) {
        await updatePopup(item.id, fd, config);
      } else {
        await createPopup(fd, config);
      }
      toast.success(isEdit ? "Popup atualizado" : "Popup criado");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erro ao salvar");
    } finally {
      setLoading(false);
    }
  };

  const getFilterDef = (type) => FILTER_TYPES.find((f) => f.value === type);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>
        {isEdit ? "Editar Pop-up" : "Novo Pop-up"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>

          {/* Título e tipo */}
          <Grid item xs={12} sm={8}>
            <TextField label="Título *" fullWidth value={form.title}
              onChange={handleField("title")} variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Tipo / Cor" select fullWidth value={form.type}
              onChange={handleField("type")} variant="outlined" size="small">
              {POPUP_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Conteúdo */}
          <Grid item xs={12}>
            <Typography className={classes.sectionLabel}>Mensagem</Typography>
            <div className={classes.quillWrapper}>
              <ReactQuill
                theme="snow"
                value={form.content}
                onChange={(val) => setForm((prev) => ({ ...prev, content: val }))}
                style={{ background: "#fff" }}
              />
            </div>
          </Grid>

          {/* Imagem upload e botão */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.sectionLabel} style={{ marginTop: 0 }}>Imagem (opcional)</Typography>
            <input
              type="file"
              accept="image/*"
              ref={imageInputRef}
              style={{ display: "none" }}
              onChange={handleImageChange}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddPhotoAlternateIcon />}
              onClick={() => imageInputRef.current.click()}
            >
              {imagePreview ? "Trocar imagem" : "Selecionar imagem"}
            </Button>
            {imagePreview && (
              <img src={imagePreview} alt="Preview" className={classes.imagePreview} />
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Texto do botão" fullWidth value={form.buttonText}
              onChange={handleField("buttonText")} variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="URL do botão (opcional)" fullWidth value={form.buttonUrl}
              onChange={handleField("buttonUrl")} variant="outlined" size="small"
              placeholder="https://..." />
          </Grid>

          {/* Datas */}
          <Grid item xs={12} sm={6}>
            <TextField label="Exibir a partir de" type="datetime-local" fullWidth
              value={form.startDate} onChange={handleField("startDate")}
              variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Exibir até" type="datetime-local" fullWidth
              value={form.endDate} onChange={handleField("endDate")}
              variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
          </Grid>

          {/* Opções */}
          <Grid item xs={12} sm={6} style={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={<Switch checked={form.active} onChange={handleToggle("active")} color="primary" />}
              label="Ativo" />
          </Grid>
          <Grid item xs={12} sm={6} style={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={<Switch checked={form.dismissible} onChange={handleToggle("dismissible")} color="primary" />}
              label="Empresa pode fechar sem clicar no botão" />
          </Grid>

          {/* Filtros */}
          <Grid item xs={12}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <Typography className={classes.sectionLabel} style={{ marginTop: 0, marginBottom: 0 }}>
                Filtros de exibição
              </Typography>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography variant="caption" color="textSecondary">
                  {filters.conditions.length > 1 ? "Combinar condições com:" : ""}
                </Typography>
                {filters.conditions.length > 1 && (
                  <Chip
                    label={filters.operator === "AND" ? "E (todas)" : "OU (qualquer)"}
                    size="small"
                    color="primary"
                    className={classes.operatorChip}
                    onClick={toggleOperator}
                    title="Clique para alternar AND/OR"
                  />
                )}
              </div>
            </div>

            {filters.conditions.map((cond, idx) => {
              const def = getFilterDef(cond.type) || {};
              return (
                <div key={idx} className={classes.filterRow}>
                  {idx > 0 && (
                    <Typography variant="caption" style={{ fontWeight: 700, color: "#6b7280", minWidth: 24 }}>
                      {filters.operator}
                    </Typography>
                  )}

                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    value={cond.type}
                    onChange={(e) => { const v = e.target.value; updateCondition(idx, "type", v); }}
                    className={classes.filterType}
                  >
                    {FILTER_TYPES.map((ft) => (
                      <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
                    ))}
                  </TextField>

                  {def.hasValue === "number" && (
                    <TextField
                      type="number"
                      size="small"
                      variant="outlined"
                      placeholder={def.placeholder || "Dias"}
                      value={cond.value || ""}
                      onChange={(e) => { const v = e.target.value; updateCondition(idx, "value", Number(v)); }}
                      className={classes.filterValue}
                      inputProps={{ min: 1 }}
                      label="dias"
                    />
                  )}

                  {def.hasValue === "plan" && (
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      value={cond.value || ""}
                      onChange={(e) => { const v = e.target.value; updateCondition(idx, "value", v); }}
                      className={classes.filterValue}
                      label="Plano"
                    >
                      {plans.map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                      ))}
                    </TextField>
                  )}

                  {def.hasValue === "segment" && (
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      value={cond.value || ""}
                      onChange={(e) => { const v = e.target.value; updateCondition(idx, "value", v); }}
                      className={classes.filterValue}
                      label="Segmento"
                    >
                      {SEGMENTS.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </TextField>
                  )}

                  {def.hasValue === "ids" && (
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="IDs separados por vírgula"
                      value={Array.isArray(cond.value) ? cond.value.join(",") : (cond.value || "")}
                      onChange={(e) => {
                        const v = e.target.value;
                        const ids = v.split(",").map(s => s.trim()).filter(Boolean).map(Number).filter(n => !isNaN(n));
                        updateCondition(idx, "value", ids);
                      }}
                      className={classes.filterValue}
                      style={{ minWidth: 200 }}
                    />
                  )}

                  <IconButton size="small" onClick={() => removeCondition(idx)} style={{ marginLeft: "auto" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              );
            })}

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addCondition}
              className={classes.addFilterBtn}
              variant="outlined"
            >
              Adicionar condição
            </Button>

            <div className={classes.previewBox}>
              <Tooltip title="Calcula quantas empresas receberiam este pop-up agora">
                <Button
                  size="small"
                  startIcon={previewLoading ? <CircularProgress size={14} /> : <VisibilityIcon />}
                  onClick={handlePreview}
                  disabled={previewLoading}
                  variant="outlined"
                  style={{ textTransform: "none" }}
                >
                  Prévia de alcance
                </Button>
              </Tooltip>
              {previewResult !== null && (
                <Typography variant="body2" style={{ color: "#0369a1", fontWeight: 600 }}>
                  {previewResult.count} de {previewResult.total} empresa(s) receberiam este pop-up
                </Typography>
              )}
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : (isEdit ? "Salvar" : "Criar")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyPopupFormModal;
